import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/img/logo/logo.png';
import FixedPlugin from 'components/fixedPlugin/FixedPlugin';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  
  const { signIn, handleConfirmSignUp, isAuthenticated, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect authenticated users away from sign-in page
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (location.state?.message) {
      setInfo(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      // First check if there's already a signed-in user and sign them out
      if (isAuthenticated) {
        await signOut();
      }

      const result = await signIn(email, password);
      
      if (result.success) {
        navigate('/');
      } else {
        if (result.nextStep === 'CONFIRM_SIGN_UP') {
          setShowConfirmation(true);
          setInfo('Please check your email for a verification code.');
        } else {
          setError(result.error || 'Sign in failed');
        }
      }
    } catch (error) {
      // Handle the "already signed in" error specifically
      if (error.message && error.message.includes('already a signed in user')) {
        // Sign out the current user and retry
        await signOut();
        setInfo('Previous session detected. Please sign in again.');
        setIsLoading(false);
        return;
      }
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationSubmit = async (e) => {
    e.preventDefault();
    if (!confirmationCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const confirmResult = await handleConfirmSignUp(email, confirmationCode);
      if (confirmResult.success) {
        // Now that the user is confirmed, try signing in again.
        const signInResult = await signIn(email, password);
        if (signInResult.success) {
          navigate('/');
        } else {
          setShowConfirmation(false);
          setError(signInResult.error || 'Sign in failed after confirmation. Please try again.');
        }
      } else {
        setError(confirmResult.error || 'Invalid verification code.');
      }
    } catch (error) {
      setError('An unexpected error occurred during confirmation.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = () => {
    navigate('/signup');
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <FixedPlugin />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img className="h-12 w-auto" src={logo} alt="ValuePay.ai" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-700 dark:text-gray-300">
          {showConfirmation ? 'Verify your account' : 'Sign in to your account'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {info && (
              <div className="mb-4 text-sm text-green-600 bg-green-50 p-3 rounded-md">
                {info}
              </div>
            )}
            {error && (
              <div className="mb-4 text-sm text-red-600">
                {error}
              </div>
            )}

          {showConfirmation ? (
            <form onSubmit={handleConfirmationSubmit}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700">
                    Verification Code
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmationCode"
                      name="confirmationCode"
                      type="text"
                      required
                      value={confirmationCode}
                      onChange={(e) => setConfirmationCode(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter the code from your email"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Verifying...' : 'Verify and Sign In'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignIn}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600/50 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/70 focus:border-transparent bg-white dark:bg-gray-800/50 text-gray-900 dark:text-white"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600/50 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/70 focus:border-transparent bg-white dark:bg-gray-800/50 text-gray-900 dark:text-white"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-700">
              New user?{' '}
              <button
                type="button"
                onClick={handleCreateAccount}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Create an account
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;